/**
 * Database objects in the SQL completion: the tables (with their columns), views, procedures and functions of the
 * product databases, read from the static catalogue scripts/build-catalog.mjs generates out of the DDL kept in
 * packages/rp-database/sql (see schemaCatalog.generated.ts). The package itself fetches nothing from a server; the
 * place of use may hand in a catalogue of its own (`options.catalog`, also as a function read on every request),
 * e.g. the static one with the objects of the customer databases merged in by mergeSchemaCatalog(). Without that
 * the objects a customer added on their own are not known; they still come up as words of the document.
 *
 * The SQL of the application names the databases by placeholders, so the objects are offered
 *  - after `{db_super}.` / `{db_master}.` / `{db}.` ... (the schema `dbo` and the objects inserted as `dbo.NAME`),
 *  - after `{db_super}.dbo.` and after a bare `dbo.` (the database the SQL runs in, `options.database`),
 *  - after `alias.` or `TABLE.` the columns of the table the document's FROM/JOIN/UPDATE binds to that name,
 *  - without a prefix the objects of the database the SQL runs in.
 * A literal database name (`Reporting_01.dbo.`) gets nothing: the SQL of the application is written with placeholders.
 */
import { CodeCompletionContext, CodeCompletionItem, CodeCompletionProvider } from './codeCompletion';
import { SQL_SCHEMA_CATALOG } from './schemaCatalog.generated';

export type SqlDatabaseRole = 'data' | 'master' | 'super' | 'mdm' | 'etl';

export type SqlSchemaColumn = [name: string, type: string];
/** [name without '@', type, default value as written] */
export type SqlSchemaParameter = [name: string, type: string, defaultValue?: string];
export type SqlSchemaTable = [name: string, columns: SqlSchemaColumn[]];
/** views come without columns: the DDL would need a SQL parser */
export type SqlSchemaView = [name: string, columns?: SqlSchemaColumn[]];
/** returns: 'table' for a table-valued function, the type of a scalar one, undefined for a procedure */
export type SqlSchemaRoutine = [name: string, parameters: SqlSchemaParameter[], returns?: string];
export type SqlSchemaDatabase = {
	tables: SqlSchemaTable[],
	views: SqlSchemaView[],
	procedures: SqlSchemaRoutine[],
	functions: SqlSchemaRoutine[],
};
/** object names outside dbo carry their schema (`clr.RemoteExec`) */
export type SqlSchemaCatalog = Partial<Record<SqlDatabaseRole, SqlSchemaDatabase>>;

/** the {placeholders} naming a database: the ETL engine's `{db}` and the Deployment module's `{db_data}` are the same one */
export const SQL_DATABASE_PLACEHOLDERS: Record<string, SqlDatabaseRole> = {
	db: 'data',
	db_data: 'data',
	db_master: 'master',
	db_super: 'super',
	db_mdm: 'mdm',
	db_etl: 'etl',
};

export type SqlSchemaOptions = {
	/** database the names without a prefix resolve in (the one the SQL runs in); null offers objects only after a `{db_*}.` prefix */
	database?: SqlDatabaseRole | null,
	/**
	 * the objects (default: the generated catalogue of the repository); a function is read on every request, so a
	 * catalogue that arrives later (fetched from a server) is picked up without a new provider
	 */
	catalog?: SqlSchemaCatalog | (() => SqlSchemaCatalog),
};

export type SqlSchemaCompletion = {
	triggerCharacters: string[],
	/** items of the qualified name before the cursor (`{db_x}.`, `dbo.`, `alias.`); null when the cursor is not behind a dot */
	qualified(context: CodeCompletionContext): CodeCompletionItem[] | null,
	/** objects of the database the SQL runs in, offered among the other unqualified items */
	unqualified(context: CodeCompletionContext): CodeCompletionItem[],
};

/** what the text before the cursor refers to */
export type SqlSchemaReference =
	| { kind: 'database', database: SqlDatabaseRole }
	| { kind: 'schema', database: SqlDatabaseRole, schema: string }
	| { kind: 'table', database: SqlDatabaseRole, table: string }
	| { kind: 'unknown' };

const SORT_PREFIX = { schema: '0', exec: '0', object: '2' };

const IDENTIFIER = String.raw`(?:\[([^\]]+)\]|([\w#$]+))`;
/** `{db_x}.` or `{db_x}.schema.` right before the cursor */
const PLACEHOLDER_REFERENCE_RE = new RegExp(String.raw`\{(\w+)\}\s*\.\s*(?:${IDENTIFIER}\s*\.\s*)?$`);
/** `name.` right before the cursor, with the optional part before it (`db.schema.`) */
const NAME_REFERENCE_RE = new RegExp(String.raw`(?:${IDENTIFIER}\s*\.\s*)?${IDENTIFIER}\s*\.\s*$`);
/** the tables of FROM / JOIN / UPDATE / INTO with their alias */
const SOURCE_RE = new RegExp(String.raw`\b(?:from|join|update|into)\s+((?:\{\w+\}|\[[^\]]+\]|[\w#$]+)(?:\s*\.\s*(?:\[[^\]]+\]|[\w#$]*)){0,2})`
	+ String.raw`(?:\s+(?:as\s+)?(?!(?:on|where|left|right|inner|outer|cross|full|join|set|select|group|order|having|union|with|values|output|option)\b)${IDENTIFIER})?`, 'gi');
const EXEC_RE = /\b(?:exec|execute)\s+\S*$/i;
/** documents beyond this size are not scanned for aliases */
const ALIAS_SCAN_MAX_LENGTH = 1_000_000;

const identifier = (bracketed: string, plain: string) => (bracketed ?? plain ?? '').trim();

function splitSchema(name: string): { schema: string, name: string } {
	const dot = name.indexOf('.');
	return dot < 0 ? { schema: 'dbo', name } : { schema: name.substring(0, dot), name: name.substring(dot + 1) };
}

function parameterText([name, type, defaultValue]: SqlSchemaParameter): string {
	return `@${name} ${type}${defaultValue !== undefined ? ` = ${defaultValue}` : ''}`;
}

function routineSignature([, parameters, returns]: SqlSchemaRoutine): string {
	const list = parameters.map(parameterText).join(', ');
	return returns ? `(${list}) returns ${returns}` : (list ? ` ${list}` : '');
}

/** `f_X(${1:@a}, ${2:@b})` */
function functionSnippet(name: string, parameters: SqlSchemaParameter[]): string {
	return name + '(' + parameters.map((p, i) => '${' + (i + 1) + ':@' + p[0] + '}').join(', ') + ')';
}

/** `p_X @a = ${1:a}, @b = ${2:b}` with the required parameters (the optional ones are in the documentation) */
function procedureSnippet(name: string, parameters: SqlSchemaParameter[]): string {
	const required = parameters.filter(p => p[2] === undefined);
	if (!required.length) return name;
	return name + ' ' + required.map((p, i) => '@' + p[0] + ' = ${' + (i + 1) + ':' + p[0] + '}').join(', ');
}

function columnsDocumentation(columns: SqlSchemaColumn[] | undefined): string {
	if (!columns?.length) return '';
	return '\n\n```\n' + columns.map(([name, type]) => `${name} ${type}`).join('\n') + '\n```';
}

function parametersDocumentation(parameters: SqlSchemaParameter[]): string {
	if (!parameters.length) return '';
	return '\n\n```\n' + parameters.map(parameterText).join('\n') + '\n```';
}

/**
 * The aliases of the document: `from {db_super}.dbo.V_ACCOUNTING B` binds `b` to that view, `from D_ACCOUNT` binds
 * the table to its own name. A literal database name (`Reporting_01.dbo.X`) binds nothing.
 */
export function resolveSqlAliases(content: string, home: SqlDatabaseRole | null): Map<string, { database: SqlDatabaseRole, table: string }> {
	const aliases = new Map<string, { database: SqlDatabaseRole, table: string }>();
	if (!content || content.length > ALIAS_SCAN_MAX_LENGTH) return aliases;
	const explicit = new Set<string>();
	for (const match of content.matchAll(SOURCE_RE)) {
		const parts = match[1].split('.').map(part => part.trim().replace(/^\[|\]$/g, ''));
		const table = parts[parts.length - 1];
		if (!table || table.startsWith('#')) continue;
		let database: SqlDatabaseRole | null = home;
		if (parts.length === 3) {
			const placeholder = /^\{(\w+)\}$/.exec(parts[0]);
			database = placeholder ? (SQL_DATABASE_PLACEHOLDERS[placeholder[1].toLowerCase()] ?? null) : null;
		}
		if (!database) continue;
		const alias = identifier(match[2], match[3]);
		const key = (alias || table).toLowerCase();
		// `update T set ... from D_ACCOUNT T`: the alias of the FROM wins over the bare name of the UPDATE
		if (aliases.has(key) && (!alias || explicit.has(key))) continue;
		aliases.set(key, { database, table });
		if (alias) explicit.add(key);
	}
	return aliases;
}

/**
 * What the qualified name before the word being typed refers to; null when there is no dot before the word.
 * `catalog` resolves a bare `NAME.` to a table of the home database when the document binds no alias to it.
 */
export function resolveSchemaReference(context: CodeCompletionContext, home: SqlDatabaseRole | null, catalog: SqlSchemaCatalog): SqlSchemaReference | null {
	const before = context.linePrefix.substring(0, context.linePrefix.length - context.word.length);
	if (!/\.\s*$/.test(before)) return null;

	const placeholder = PLACEHOLDER_REFERENCE_RE.exec(before);
	if (placeholder) {
		const database = SQL_DATABASE_PLACEHOLDERS[placeholder[1].toLowerCase()];
		if (!database) return { kind: 'unknown' };
		const schema = identifier(placeholder[2], placeholder[3]);
		return schema ? { kind: 'schema', database, schema } : { kind: 'database', database };
	}

	const name = NAME_REFERENCE_RE.exec(before);
	if (!name) return { kind: 'unknown' };
	// `db.schema.` is a literal database name, `schema.table.` is never typed; both get nothing
	if (identifier(name[1], name[2])) return { kind: 'unknown' };
	const word = identifier(name[3], name[4]);
	if (word.startsWith('#')) return { kind: 'unknown' };

	const objects = home ? catalog[home] : undefined;
	if (home && objects && hasSchema(objects, word)) return { kind: 'schema', database: home, schema: word };

	const alias = resolveSqlAliases(context.getContent(), home).get(word.toLowerCase());
	if (alias) return { kind: 'table', database: alias.database, table: alias.table };
	if (home && objects && findColumns(objects, word)) return { kind: 'table', database: home, table: word };
	return { kind: 'unknown' };
}

function hasSchema(objects: SqlSchemaDatabase, schema: string): boolean {
	const lower = schema.toLowerCase();
	if (lower === 'dbo') return true;
	return [...objects.tables, ...objects.views, ...objects.procedures, ...objects.functions].some(o => splitSchema(o[0]).schema.toLowerCase() === lower);
}

function findColumns(objects: SqlSchemaDatabase, table: string): SqlSchemaColumn[] | null {
	const lower = table.toLowerCase();
	const found = [...objects.tables, ...objects.views].find(o => o[0].toLowerCase() === lower);
	return found?.[1] ?? null;
}

export function createSchemaCompletion(options: SqlSchemaOptions = {}): SqlSchemaCompletion {
	const source = typeof options.catalog === 'function' ? options.catalog : () => (options.catalog as SqlSchemaCatalog | undefined) ?? SQL_SCHEMA_CATALOG;
	const home = options.database === undefined ? 'data' : options.database;
	const objectCache = new Map<string, CodeCompletionItem[]>();
	const columnCache = new Map<string, CodeCompletionItem[]>();
	let catalog = source();

	/** the catalogue of the moment; the items built from the previous one are dropped when another object comes */
	const current = (): SqlSchemaCatalog => {
		const next = source();
		if (next !== catalog) {
			catalog = next;
			objectCache.clear();
			columnCache.clear();
		}
		return catalog;
	};

	/** the objects of a schema of a database; `prefix` is put before the inserted name (`dbo.` after a bare `{db_x}.`) */
	const objectItems = (database: SqlDatabaseRole, schema: string, prefix: string, exec: boolean): CodeCompletionItem[] => {
		const key = `${database}|${schema.toLowerCase()}|${prefix}|${exec ? 1 : 0}`;
		let items = objectCache.get(key);
		if (items) return items;
		items = [];
		const objects = catalog[database];
		if (objects) {
			const placeholder = Object.entries(SQL_DATABASE_PLACEHOLDERS).find(([, role]) => role === database)?.[0];
			const location = (name: string) => `{${placeholder}}.${schema}.${name}`;
			const inSchema = <T extends [string, ...unknown[]]>(list: T[]): [string, T][] => list
				.map(o => [splitSchema(o[0]), o] as const)
				.filter(([s]) => s.schema.toLowerCase() === schema.toLowerCase())
				.map(([s, o]) => [s.name, o]);

			for (const [name, [, columns]] of inSchema(objects.tables)) {
				items.push({ label: name, kind: 'table', detail: 'table', insertText: prefix + name, sortText: SORT_PREFIX.object + name,
					documentation: `**table** \`${location(name)}\`${columnsDocumentation(columns)}` });
			}
			for (const [name, [, columns]] of inSchema(objects.views)) {
				items.push({ label: name, kind: 'table', detail: 'view', insertText: prefix + name, sortText: SORT_PREFIX.object + name,
					documentation: `**view** \`${location(name)}\`${columnsDocumentation(columns)}` });
			}
			for (const [name, routine] of inSchema(objects.functions)) {
				items.push({ label: name, kind: 'function', detail: name + routineSignature(routine), snippet: true,
					insertText: prefix + functionSnippet(name, routine[1]), sortText: SORT_PREFIX.object + name,
					documentation: `**${routine[2] === 'table' ? 'table-valued ' : ''}function** \`${location(name)}\`${parametersDocumentation(routine[1])}` });
			}
			for (const [name, routine] of inSchema(objects.procedures)) {
				items.push({ label: name, kind: 'function', detail: 'procedure' + routineSignature(routine), snippet: exec,
					insertText: prefix + (exec ? procedureSnippet(name, routine[1]) : name), sortText: (exec ? SORT_PREFIX.exec : SORT_PREFIX.object) + name,
					documentation: `**procedure** \`${location(name)}\`${parametersDocumentation(routine[1])}` });
			}
		}
		objectCache.set(key, items);
		return items;
	};

	const schemaItems = (database: SqlDatabaseRole): CodeCompletionItem[] => {
		const objects = catalog[database];
		if (!objects) return [];
		const schemas = new Set<string>(['dbo']);
		for (const list of [objects.tables, objects.views, objects.procedures, objects.functions]) {
			for (const o of list) schemas.add(splitSchema(o[0]).schema);
		}
		return [...schemas].map(schema => ({ label: schema, kind: 'module' as const, detail: 'schema', sortText: SORT_PREFIX.schema + schema }));
	};

	const columnItems = (database: SqlDatabaseRole, table: string): CodeCompletionItem[] => {
		const key = `${database}|${table.toLowerCase()}`;
		let items = columnCache.get(key);
		if (items) return items;
		const objects = catalog[database];
		const columns = objects ? findColumns(objects, table) : null;
		items = (columns ?? []).map(([name, type], i) => ({
			label: name, kind: 'field' as const, detail: type, sortText: String(i).padStart(4, '0'),
		}));
		columnCache.set(key, items);
		return items;
	};

	return {
		triggerCharacters: ['.'],
		qualified(context) {
			const reference = resolveSchemaReference(context, home, current());
			if (!reference) return null;
			const exec = EXEC_RE.test(context.linePrefix);
			switch (reference.kind) {
				case 'database':
					return [...schemaItems(reference.database), ...objectItems(reference.database, 'dbo', 'dbo.', exec)];
				case 'schema':
					return objectItems(reference.database, reference.schema, '', exec);
				case 'table':
					return columnItems(reference.database, reference.table);
				default:
					return [];
			}
		},
		unqualified(context) {
			if (!home) return [];
			current();
			return objectItems(home, 'dbo', '', EXEC_RE.test(context.linePrefix));
		},
	};
}

/**
 * The static catalogue with the objects read from the live databases put in its place, database by database: the
 * tables and views of a live database replace the static ones (they are the truth, including the customer's own
 * objects and the columns of the views), its procedures and functions as well, except that a routine the static
 * catalogue knows under the same name and parameters keeps the static entry: the server's metadata carries no
 * parameter defaults, the DDL of the repository does, and the defaults decide what the `exec` snippet asks for.
 * Databases the live catalogue does not cover stay as they are.
 */
export function mergeSchemaCatalog(base: SqlSchemaCatalog, live: Partial<Record<SqlDatabaseRole, SqlSchemaDatabase | null | undefined>>): SqlSchemaCatalog {
	const merged: SqlSchemaCatalog = { ...base };
	for (const [role, objects] of Object.entries(live) as [SqlDatabaseRole, SqlSchemaDatabase | null | undefined][]) {
		if (!objects) continue;
		const known = base[role];
		merged[role] = {
			tables: objects.tables ?? [],
			views: objects.views ?? [],
			procedures: preferKnownRoutines(objects.procedures ?? [], known?.procedures),
			functions: preferKnownRoutines(objects.functions ?? [], known?.functions),
		};
	}
	return merged;
}

function preferKnownRoutines(live: SqlSchemaRoutine[], known: SqlSchemaRoutine[] | undefined): SqlSchemaRoutine[] {
	if (!known?.length) return live;
	const byName = new Map(known.map(routine => [routine[0].toLowerCase(), routine]));
	return live.map(routine => {
		const match = byName.get(routine[0].toLowerCase());
		return match && sameParameters(match, routine) ? match : routine;
	});
}

const sameParameters = (a: SqlSchemaRoutine, b: SqlSchemaRoutine) =>
	a[1].length === b[1].length && a[1].every((p, i) => p[0].toLowerCase() === b[1][i][0].toLowerCase());

/** The database objects as a provider of their own, for editors without the shared T-SQL provider. */
export function createSchemaCompletionProvider(options: SqlSchemaOptions = {}): CodeCompletionProvider {
	const schema = createSchemaCompletion(options);
	return {
		triggerCharacters: schema.triggerCharacters,
		provide: context => schema.qualified(context) ?? schema.unqualified(context),
	};
}
